// src/pages/dashboard/AdminBlogs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageShell from "./PageShell";
import { getBlogs, getBlogById, createBlog } from "../../core/api/blogsApi";

function excerpt(text, n = 120) {
  const t = (text || "").toString().replace(/\s+/g, " ").trim();
  if (!t) return "—";
  return t.length > n ? t.slice(0, n) + "…" : t;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function AdminBlogs() {
  const navigate = useNavigate();
  const params = useParams();

  // route mode decide:
  // /dashboard/blogs        => list
  // /dashboard/blogs/new    => create
  // /dashboard/blogs/:id    => detail
  const path = window.location.pathname;
  const mode = useMemo(() => {
    if (path.endsWith("/blogs/new")) return "create";
    if (params?.id) return "detail";
    return "list";
  }, [path, params?.id]);

  // --------------------
  // LIST STATE
  // --------------------
  const [blogs, setBlogs] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState("");

  const loadBlogs = async () => {
    try {
      setListLoading(true);
      setListErr("");
      const data = await getBlogs();
      const items = Array.isArray(data) ? data : data?.results || [];
      setBlogs(items);
    } catch {
      setListErr("Unable to load blogs right now. Please try again.");
    } finally {
      setListLoading(false);
    }
  };

  // --------------------
  // DETAIL STATE
  // --------------------
  const [blog, setBlog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");

  const loadBlogDetail = async (id) => {
    try {
      setDetailLoading(true);
      setDetailErr("");
      const data = await getBlogById(id);
      setBlog(data || null);
    } catch {
      setDetailErr("Unable to load blog. Please try again.");
      setBlog(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // --------------------
  // CREATE STATE
  // --------------------
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [createLoading, setCreateLoading] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const canSubmit = useMemo(() => {
    return title.trim() && content.trim() && !createLoading;
  }, [title, content, createLoading]);

  const resetCreateForm = () => {
    setTitle("");
    setContent("");
    setAuthor("");
    setImageFile(null);
    setCreateErr("");
    setFieldErrors({});
  };

  const onCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateErr("");
    setFieldErrors({});

    if (!title.trim() || !content.trim()) {
      setCreateErr("Title and Content are required.");
      return;
    }

    try {
      setCreateLoading(true);

      const payload = {
        title: title.trim(),
        content: content.trim(),
      };
      if (author.trim()) payload.author = author.trim();
      if (imageFile) payload.image = imageFile;

      const created = await createBlog(payload);
      const newId = created?.id ?? created?.pk;

      resetCreateForm();

      if (newId) navigate(`/dashboard/blogs/${newId}`, { replace: true });
      else navigate("/dashboard/blogs", { replace: true });
    } catch (e2) {
      const data = e2?.response?.data;
      if (data && typeof data === "object") {
        setFieldErrors(data);
        setCreateErr("Please fix highlighted fields.");
      } else {
        setCreateErr("Unable to create blog. Please try again.");
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // --------------------
  // EFFECT: load data based on mode
  // --------------------
  useEffect(() => {
    if (mode === "list") loadBlogs();
    if (mode === "detail" && params?.id) loadBlogDetail(params.id);

    // create mode => no fetch needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, params?.id]);

  // --------------------
  // derived views
  // --------------------
  const rows = useMemo(() => {
    return (blogs || []).map((b) => {
      const id = b?.id ?? b?.pk;
      const title = b?.title || "Untitled";
      const content = b?.content || b?.description || b?.body || "";
      const createdAt = b?.created_at || b?.createdAt || b?.created;
      return { id, title, content, createdAt };
    });
  }, [blogs]);

  const detailView = useMemo(() => {
    const b = blog || {};
    return {
      id: b?.id ?? b?.pk,
      title: b?.title || "Untitled",
      content: b?.content || b?.description || b?.body || "",
      image: b?.image || b?.image_url || null,
      createdAt: b?.created_at || b?.createdAt || b?.created || "",
      author: b?.author || "",
    };
  }, [blog]);

  return (
    <PageShell title="Blogs">
      {/* ✅ STRICT RULE: No API URLs / JSON debug text rendered here */}

      {/* ===================== LIST ===================== */}
      {mode === "list" ? (
        <div>
          <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
            <div>
{/*               <h2 className="text-xl sm:text-2xl font-bold text-secondary"> */}
{/*                 Blogs */}
{/*               </h2> */}
              <p className="mt-1 text-sm text-secondary/80">
                Create and manage blog posts.
              </p>
            </div>

            <button
              onClick={() => navigate("/dashboard/blogs/new")}
              className="w-full sm:w-auto rounded-xl bg-secondary text-accent px-4 py-2.5 font-semibold shadow-sm hover:bg-primary hover:text-secondary transition"
            >
              + Create Blog
            </button>
          </div>

          <div className="mt-6">
            {listLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-16 rounded-xl border border-secondary/20 bg-white/60 animate-pulse"
                  />
                ))}
              </div>
            ) : listErr ? (
              <div className="rounded-xl border border-red-300 bg-red-50 p-4">
                <p className="text-red-700 text-sm">{listErr}</p>
                <button
                  onClick={loadBlogs}
                  className="mt-3 rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-100 transition"
                >
                  Retry
                </button>
              </div>
            ) : rows.length === 0 ? (
              <div className="rounded-xl border border-secondary/20 bg-white/60 p-6 text-secondary">
                <p className="font-semibold">No blogs yet</p>
                <p className="mt-1 text-sm text-secondary/80">
                  Create your first blog to see it listed here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-secondary/20 bg-white/60">
                <table className="w-full text-left">
                  <thead className="bg-white/70 border-b border-secondary/20">
                    <tr>
                      <th className="px-4 py-3 text-sm font-semibold text-secondary">
                        Title
                      </th>
                      <th className="px-4 py-3 text-sm font-semibold text-secondary">
                        Excerpt
                      </th>
                      <th className="px-4 py-3 text-sm font-semibold text-secondary">
                        Created
                      </th>
                      <th className="px-4 py-3 text-sm font-semibold text-secondary text-right">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b border-secondary/10 hover:bg-primary/10 transition"
                      >
                        <td className="px-4 py-3 text-secondary font-semibold">
                          {r.title}
                        </td>
                        <td className="px-4 py-3 text-secondary/80 text-sm">
                          {excerpt(r.content, 120)}
                        </td>
                        <td className="px-4 py-3 text-secondary/70 text-sm">
                          {formatDate(r.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/dashboard/blogs/${r.id}`}
                            className="inline-flex items-center justify-center rounded-lg border border-secondary/30 px-3 py-2 text-sm text-secondary hover:bg-primary/20 transition"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* ===================== CREATE ===================== */}
      {mode === "create" ? (
        <div>
          <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-secondary">
                Create Blog
              </h2>
              <p className="mt-1 text-sm text-secondary/80">
                Add a new blog post.
              </p>
            </div>

            <Link
              to="/dashboard/blogs"
              className="w-full sm:w-auto rounded-xl border border-secondary/30 px-4 py-2.5 text-secondary font-semibold hover:bg-primary/20 transition text-center"
            >
              ← Back
            </Link>
          </div>

          <form onSubmit={onCreateSubmit} className="mt-6 space-y-5">
            {createErr ? (
              <div className="rounded-xl border border-red-300 bg-red-50 p-4">
                <p className="text-red-700 text-sm">{createErr}</p>
              </div>
            ) : null}

            <div className="rounded-2xl border border-secondary/20 bg-white/60 p-5">
              <label className="block text-sm font-semibold text-secondary">
                Title <span className="text-red-600">*</span>
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter blog title"
                className="mt-2 w-full rounded-xl border border-secondary/30 bg-white px-4 py-3 text-secondary outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
              />
              {fieldErrors?.title ? (
                <p className="mt-2 text-xs text-red-600">
                  {String(fieldErrors.title)}
                </p>
              ) : null}

              <label className="block text-sm font-semibold text-secondary mt-4">
                Author (optional)
              </label>
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Author name"
                className="mt-2 w-full rounded-xl border border-secondary/30 bg-white px-4 py-3 text-secondary outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
              />
              {fieldErrors?.author ? (
                <p className="mt-2 text-xs text-red-600">
                  {String(fieldErrors.author)}
                </p>
              ) : null}

              <label className="block text-sm font-semibold text-secondary mt-4">
                Content <span className="text-red-600">*</span>
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={10}
                placeholder="Write your content..."
                className="mt-2 w-full rounded-xl border border-secondary/30 bg-white px-4 py-3 text-secondary outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/20"
              />
              {fieldErrors?.content ? (
                <p className="mt-2 text-xs text-red-600">
                  {String(fieldErrors.content)}
                </p>
              ) : null}

              <label className="block text-sm font-semibold text-secondary mt-4">
                Image (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                className="mt-2 w-full rounded-xl border border-secondary/30 bg-white px-4 py-3 text-secondary"
              />
              {imageFile ? (
                <p className="mt-2 text-xs text-secondary/70">
                  Selected: {imageFile.name}
                </p>
              ) : null}
              {fieldErrors?.image ? (
                <p className="mt-2 text-xs text-red-600">
                  {String(fieldErrors.image)}
                </p>
              ) : null}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => navigate("/dashboard/blogs")}
                className="rounded-xl border border-secondary/30 px-4 py-2.5 text-secondary font-semibold hover:bg-primary/20 transition"
                disabled={createLoading}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-xl bg-secondary text-accent px-5 py-2.5 font-semibold shadow-sm hover:bg-primary hover:text-secondary transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {createLoading ? "Creating..." : "Create Blog"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {/* ===================== DETAIL ===================== */}
      {mode === "detail" ? (
        <div>
          <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-secondary">
                {detailLoading ? "Loading..." : detailView.title}
              </h2>
              <div className="mt-1 text-sm text-secondary/70 flex flex-wrap gap-3">
                {detailView.author ? (
                  <span>By {String(detailView.author)}</span>
                ) : null}
                {detailView.createdAt ? (
                  <span>{formatDate(detailView.createdAt)}</span>
                ) : null}
              </div>
            </div>

            <Link
              to="/dashboard/blogs"
              className="w-full sm:w-auto rounded-xl border border-secondary/30 px-4 py-2.5 text-secondary font-semibold hover:bg-primary/20 transition text-center"
            >
              ← Back
            </Link>
          </div>

          <div className="mt-6">
            {detailLoading ? (
              <div className="space-y-3">
                <div className="h-10 rounded-xl border border-secondary/20 bg-white/60 animate-pulse" />
                <div className="h-56 rounded-2xl border border-secondary/20 bg-white/60 animate-pulse" />
              </div>
            ) : detailErr ? (
              <div className="rounded-xl border border-red-300 bg-red-50 p-4">
                <p className="text-red-700 text-sm">{detailErr}</p>
              </div>
            ) : !blog ? (
              <div className="rounded-xl border border-secondary/20 bg-white/60 p-6 text-secondary">
                Blog not found.
              </div>
            ) : (
              <div className="space-y-5">
                {detailView.image ? (
                  <div className="overflow-hidden rounded-2xl border border-secondary/20 bg-white/60">
                    <img
                      src={detailView.image}
                      alt={detailView.title}
                      className="w-full max-h-[420px] object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : null}

                <div className="rounded-2xl border border-secondary/20 bg-white/60 p-5 sm:p-6">
                  <div className="text-secondary whitespace-pre-line leading-relaxed">
                    {detailView.content || "—"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
